import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Archive, Send, Eye, HelpCircle } from "lucide-react";

export const Route = createFileRoute("/admin_/questions")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Ask Recovery Trauma — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminQuestionsPage,
});

type Row = {
  id: string;
  topic: string;
  body: string;
  share_anonymously: boolean;
  status: string;
  answer: string | null;
  created_at: string;
  published_at: string | null;
};

const STATUSES = ["new", "answered", "published", "archived"] as const;
type StatusFilter = (typeof STATUSES)[number] | "all";

const TOPIC_LABEL: Record<string, string> = {
  understanding_trauma: "Understanding trauma",
  nervous_system: "Nervous-system support",
  recovery_tools: "Recovery tools",
  platform_help: "Platform help",
  other: "Other",
};

function AdminQuestionsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("new");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const q = supabase
      .from("community_questions")
      .select("id, topic, body, share_anonymously, status, answer, created_at, published_at")
      .order("created_at", { ascending: false })
      .limit(200);
    const { data, error } = filter === "all" ? await q : await q.eq("status", filter);
    if (error) toast.error("Couldn't load questions");
    setRows((data as Row[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function update(
    row: Row,
    patch: {
      answer?: string;
      status?: string;
      answered_at?: string;
      published_at?: string | null;
    },
  ) {
    setSavingId(row.id);
    const { error } = await supabase.from("community_questions").update(patch).eq("id", row.id);
    setSavingId(null);
    if (error) {
      console.error("[admin-questions] update failed", error);
      toast.error("Couldn't save. Please try again.");
      return;
    }
    toast.success("Saved");
    void load();
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Admin
      </Link>
      <h1 className="font-display text-2xl text-foreground md:text-3xl">
        <HelpCircle className="me-2 inline h-6 w-6 text-[var(--gold-deep)]" />
        Ask Recovery Trauma™
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Review, answer, publish (anonymised) or archive member questions. Private
        questions are never shown in the community feed.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {(["new", "answered", "published", "archived", "all"] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize ${
              filter === s
                ? "border-transparent bg-foreground text-background"
                : "border-border bg-card text-foreground/75 hover:bg-accent"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--gold-deep)]" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-8 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No questions in this view.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {rows.map((r) => {
            const draft = drafts[r.id] ?? r.answer ?? "";
            return (
              <li key={r.id} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-foreground">
                    {TOPIC_LABEL[r.topic] ?? r.topic}
                  </span>
                  <span className="rounded-full border border-border px-2 py-0.5 capitalize">{r.status}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 ${
                      r.share_anonymously
                        ? "bg-emerald-100 text-emerald-900"
                        : "bg-amber-100 text-amber-900"
                    }`}
                  >
                    {r.share_anonymously ? "May be shared anonymously" : "Private — do not publish"}
                  </span>
                  <span>{new Date(r.created_at).toLocaleString("en-GB")}</span>
                </div>

                <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{r.body}</p>

                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                  rows={3}
                  placeholder="Write an educational, non-clinical answer…"
                  className="mt-3 w-full resize-none rounded-xl border border-border bg-background p-3 text-sm text-foreground outline-none focus:border-[var(--gold)]"
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={savingId === r.id || !draft.trim()}
                    onClick={() =>
                      update(r, {
                        answer: draft.trim(),
                        status: "answered",
                        answered_at: new Date().toISOString(),
                      })
                    }
                    className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    <Send className="h-3.5 w-3.5" /> Save answer
                  </button>
                  <button
                    type="button"
                    disabled={savingId === r.id || !r.share_anonymously || !draft.trim()}
                    title={
                      r.share_anonymously
                        ? "Publish anonymised question and answer"
                        : "This member asked privately — publishing is disabled"
                    }
                    onClick={() =>
                      update(r, {
                        answer: draft.trim(),
                        status: "published",
                        published_at: new Date().toISOString(),
                      })
                    }
                    className="btn-gold inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    <Eye className="h-3.5 w-3.5" /> Publish anonymised
                  </button>
                  <button
                    type="button"
                    disabled={savingId === r.id}
                    onClick={() => update(r, { status: "archived", published_at: null })}
                    className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    <Archive className="h-3.5 w-3.5" /> Archive
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
